import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  Globe,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Network,
  FileText,
  ExternalLink,
  Copy,
  Loader2,
  Ban,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

// VirusTotal API Configuration
const VIRUSTOTAL_API_KEY = "2c7f9b28d30e0b2c67c149de4367352c0af0a50be0611bcb12fbbcf84636e049";
const VIRUSTOTAL_API_BASE = "https://www.virustotal.com/vtapi/v2";

// Interface for VirusTotal API response
interface VirusTotalResponse {
  response_code: number;
  verbose_msg: string;
  detected_urls?: Array<{
    url: string;
    positives: number;
    total: number;
    scan_date: string;
  }>;
  detected_communicating_samples?: Array<{
    date: string;
    positives: number;
    total: number;
    sha256: string;
  }>;
  undetected_urls?: Array<{
    url: string;
    positives: number;
    total: number;
    scan_date: string;
  }>;
  resolutions?: Array<{
    last_resolved: string;
    ip_address: string;
  }>;
  subdomains?: string[];
  categories?: string[];
  domain_siblings?: string[];
  websense_category?: string;
  bitdefender_category?: string;
  webutation_domain_info?: {
    verdict: string;
    Adult: string;
    Safety: string;
    Malware: string;
  };
}

// Interface for Cloudflare DNS response
interface CloudflareDNSResponse {
  Status: number;
  TC: boolean;
  RD: boolean;
  RA: boolean;
  AD: boolean;
  CD: boolean;
  Question: Array<{
    name: string;
    type: number;
  }>;
  Answer: Array<{
    name: string;
    type: number;
    TTL: number;
    data: string;
  }>;
}

export interface DomainLookupResult {
  domain: string;
  ipAddresses: string[];
  location: string;
  isp: string;
  category: string;
  threatLevel: "low" | "medium" | "high" | "critical";
  reputation: number;
  lastSeen: string;
  blockCount: number;
  dnsRecords: {
    type: string;
    value: string;
    ttl: number;
  }[];
  securityInfo: {
    hasDNSSEC: boolean;
    hasSSL: boolean;
    sslGrade: string;
    malwareDetected: boolean;
    phishingDetected: boolean;
  };
}

// Rate limiting state (shared across all users)
interface ApiUsage {
  requestCount: number;
  lastReset: number;
  dailyCount: number;
  dailyReset: number;
}

const getApiUsage = (): ApiUsage => {
  const stored = localStorage.getItem('virustotal_usage');
  const now = Date.now();
  
  if (!stored) {
    return {
      requestCount: 0,
      lastReset: now,
      dailyCount: 0,
      dailyReset: now
    };
  }
  
  const usage: ApiUsage = JSON.parse(stored);
  
  // Reset minute counter every minute
  if (now - usage.lastReset >= 60000) {
    usage.requestCount = 0;
    usage.lastReset = now;
  }
  
  // Reset daily counter every day
  if (now - usage.dailyReset >= 86400000) {
    usage.dailyCount = 0;
    usage.dailyReset = now;
  }
  
  return usage;
};

const updateApiUsage = (usage: ApiUsage) => {
  localStorage.setItem('virustotal_usage', JSON.stringify(usage));
};

export function DomainLookupTool() {
  const [domain, setDomain] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<DomainLookupResult | null>(
    null
  );
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [apiUsage, setApiUsage] = useState<ApiUsage>(getApiUsage);

  // Helper function to get DNS records via Cloudflare DNS-over-HTTPS
  const getDNSRecords = async (domain: string) => {
    try {
      const types = ['A', 'AAAA', 'MX', 'TXT'];
      const dnsPromises = types.map(async (type) => {
        const response = await fetch(
          `https://cloudflare-dns.com/dns-query?name=${domain}&type=${type}`,
          {
            headers: {
              'Accept': 'application/dns-json',
            },
          }
        );
        const data: CloudflareDNSResponse = await response.json();
        return { type, data };
      });

      const results = await Promise.allSettled(dnsPromises);
      const dnsRecords: Array<{ type: string; value: string; ttl: number }> = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.data.Answer) {
          result.value.data.Answer.forEach((answer) => {
            dnsRecords.push({
              type: result.value.type,
              value: answer.data,
              ttl: answer.TTL,
            });
          });
        }
      });

      return dnsRecords;
    } catch (error) {
      console.error('DNS lookup failed:', error);
      return [];
    }
  };

  // Helper function to get IP geolocation
  const getIPLocation = async (ip: string) => {
    try {
      // Use ipapi.co which supports HTTPS and CORS
      const response = await fetch(`https://ipapi.co/${ip}/json/`);
      const data = await response.json();
      return {
        location: `${data.city || 'Unknown'}, ${data.region || 'Unknown'}, ${data.country_name || 'Unknown'}`,
        isp: data.org || 'Unknown ISP',
      };
    } catch (error) {
      console.error('IP geolocation failed:', error);
      return {
        location: 'Unknown Location',
        isp: 'Unknown ISP',
      };
    }
  };

  // Helper function to query VirusTotal
  const queryVirusTotal = async (domain: string) => {
    try {
      const response = await fetch(
        `${VIRUSTOTAL_API_BASE}/domain/report?apikey=${VIRUSTOTAL_API_KEY}&domain=${domain}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`VirusTotal API error: ${response.status}`);
      }

      const data: VirusTotalResponse = await response.json();
      return data;
    } catch (error) {
      console.error('VirusTotal query failed:', error);
      throw error;
    }
  };

  // Helper function to calculate threat level and reputation
  const calculateThreatAssessment = (vtData: VirusTotalResponse) => {
    let threatLevel: "low" | "medium" | "high" | "critical" = "low";
    let reputation = 90;
    let blockCount = 0;

    // Check detected URLs
    if (vtData.detected_urls && vtData.detected_urls.length > 0) {
      const detectedCount = vtData.detected_urls.length;
      const highPositives = vtData.detected_urls.filter(url => url.positives > 5).length;
      
      blockCount = detectedCount;
      
      if (highPositives > 10) {
        threatLevel = "critical";
        reputation = 10;
      } else if (highPositives > 5) {
        threatLevel = "high";
        reputation = 30;
      } else if (detectedCount > 5) {
        threatLevel = "medium";
        reputation = 60;
      }
    }

    // Check communicating samples (malware that contacted this domain)
    if (vtData.detected_communicating_samples && vtData.detected_communicating_samples.length > 0) {
      const malwareSamples = vtData.detected_communicating_samples.length;
      if (malwareSamples > 10) {
        threatLevel = "high";
        reputation = Math.min(reputation, 40);
      } else if (malwareSamples > 0) {
        threatLevel = "medium";
        reputation = Math.min(reputation, 70);
      }
    }

    // Check category classifications
    const category = vtData.websense_category || vtData.bitdefender_category || 
                    (vtData.categories && vtData.categories[0]) || "Technology";

    // Check webutation verdict
    if (vtData.webutation_domain_info) {
      const webutation = vtData.webutation_domain_info;
      if (webutation.Malware === "yes" || webutation.verdict === "malicious") {
        threatLevel = "critical";
        reputation = Math.min(reputation, 20);
      }
    }

    return { threatLevel, reputation, blockCount, category };
  };

  const handleSearch = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain name");
      return;
    }

    // Check rate limits before proceeding
    const currentUsage = getApiUsage();
    
    if (currentUsage.requestCount >= 4) {
      const timeUntilReset = Math.ceil((60000 - (Date.now() - currentUsage.lastReset)) / 1000);
      toast.error(`⏱️ Rate limit reached. Please wait ${timeUntilReset} seconds before next search.`);
      return;
    }
    
    if (currentUsage.dailyCount >= 500) {
      toast.error("📊 Daily API limit reached (500 requests). Please try again tomorrow.");
      return;
    }

    setIsSearching(true);

    try {
      // Step 1: Get DNS records
      console.log("🔍 Getting DNS records...");
      const dnsRecords = await getDNSRecords(domain.trim());
      
      // Extract IP addresses from A records
      const ipAddresses = dnsRecords
        .filter(record => record.type === 'A')
        .map(record => record.value);

      // Step 2: Get IP location (use first IP if available)
      let locationInfo = { location: "Unknown Location", isp: "Unknown ISP" };
      if (ipAddresses.length > 0) {
        console.log("🌍 Getting IP location...");
        locationInfo = await getIPLocation(ipAddresses[0]);
      }

      // Step 3: Query VirusTotal for threat intelligence
      console.log("🛡️ Querying VirusTotal...");
      const vtData = await queryVirusTotal(domain.trim());
      
      // Update API usage after successful VirusTotal call
      const updatedUsage = getApiUsage();
      updatedUsage.requestCount++;
      updatedUsage.dailyCount++;
      updateApiUsage(updatedUsage);
      setApiUsage(updatedUsage);
      
      // Step 4: Calculate threat assessment
      const { threatLevel, reputation, blockCount, category } = calculateThreatAssessment(vtData);

      // Step 5: Determine security info
      const securityInfo = {
        hasDNSSEC: dnsRecords.some(record => record.type === 'DS' || record.type === 'RRSIG'),
        hasSSL: true, // We'll assume HTTPS is available for now
        sslGrade: "A", // Default grade
        malwareDetected: vtData.detected_communicating_samples ? vtData.detected_communicating_samples.length > 0 : false,
        phishingDetected: vtData.detected_urls ? vtData.detected_urls.some(url => url.positives > 0) : false,
      };

      // Step 6: Compile results
      const result: DomainLookupResult = {
        domain: domain.trim(),
        ipAddresses: ipAddresses.length > 0 ? ipAddresses : ["No A records found"],
        location: locationInfo.location,
        isp: locationInfo.isp,
        category,
        threatLevel,
        reputation,
        lastSeen: new Date().toISOString(),
        blockCount,
        dnsRecords,
        securityInfo,
      };

      setSearchResult(result);
      setSearchHistory((prev) => [domain.trim(), ...prev.slice(0, 9)]);
      
      // Show appropriate success message based on threat level
      if (threatLevel === "critical" || threatLevel === "high") {
        toast.error(`⚠️ High-risk domain detected! Threat level: ${threatLevel.toUpperCase()}`);
      } else if (threatLevel === "medium") {
        toast.warning(`⚠️ Medium-risk domain. Reputation: ${reputation}/100`);
      } else {
        toast.success("✅ Domain lookup completed successfully");
      }

    } catch (error) {
      console.error("Domain lookup failed:", error);
      toast.error("Domain lookup failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case "critical":
        return "bg-red-600 text-white";
      case "high":
        return "bg-red-500 text-white";
      case "medium":
        return "bg-yellow-500 text-white";
      case "low":
        return "bg-green-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getReputationColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getSSLGradeColor = (grade: string) => {
    if (grade === "A+" || grade === "A") return "text-green-600";
    if (grade === "B" || grade === "C") return "text-yellow-600";
    return "text-red-600";
  };

  // Auto-refresh usage every 10 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setApiUsage(getApiUsage());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      {/* API Usage Information */}
      <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <CardTitle className="text-sm font-medium text-orange-800 dark:text-orange-200">
                API Usage & Limits
              </CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              VirusTotal Free Tier
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-orange-700 dark:text-orange-300">
                {apiUsage.requestCount}/4
              </div>
              <div className="text-orange-600 dark:text-orange-400 text-xs">
                Per Minute
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-orange-700 dark:text-orange-300">
                {apiUsage.dailyCount}/500
              </div>
              <div className="text-orange-600 dark:text-orange-400 text-xs">
                Today
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-orange-700 dark:text-orange-300">
                {Math.max(0, Math.ceil((60000 - (Date.now() - apiUsage.lastReset)) / 1000))}s
              </div>
              <div className="text-orange-600 dark:text-orange-400 text-xs">
                Until Reset
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-orange-700 dark:text-orange-300">
                {apiUsage.requestCount >= 4 ? "⏸️" : "✅"}
              </div>
              <div className="text-orange-600 dark:text-orange-400 text-xs">
                Status
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-orange-700 dark:text-orange-300">
            <strong>Shared Limits:</strong> All users share the same API quota. 
            Limits reset every minute (4 requests) and daily (500 requests).
          </div>
        </CardContent>
      </Card>

      {/* Search Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Domain Lookup Tool
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="domainInput" className="sr-only">
                Domain
              </Label>
              <Input
                id="domainInput"
                placeholder="Enter domain (e.g., example.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={isSearching || !domain.trim() || apiUsage.requestCount >= 4 || apiUsage.dailyCount >= 500}
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Lookup
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search History */}
      {searchHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Searches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {searchHistory.map((recentDomain, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => setDomain(recentDomain)}
                  className="text-xs"
                >
                  {recentDomain}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {searchResult && (
        <div className="space-y-6">
          {/* Overview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Domain Overview</span>
                <div className="flex items-center gap-2">
                  <Badge
                    className={getThreatLevelColor(searchResult.threatLevel)}
                  >
                    {searchResult.threatLevel.toUpperCase()} THREAT
                  </Badge>
                  <Badge variant="outline">
                    Reputation:{" "}
                    <span
                      className={getReputationColor(searchResult.reputation)}
                    >
                      {searchResult.reputation}/100
                    </span>
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label className="text-sm font-medium">Domain</Label>
                  <div className="font-mono text-sm bg-muted p-2 rounded mt-1 flex items-center justify-between">
                    {searchResult.domain}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyToClipboard(searchResult.domain)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">IP Addresses</Label>
                  <div className="space-y-1 mt-1">
                    {searchResult.ipAddresses.map((ip, index) => (
                      <div
                        key={index}
                        className="font-mono text-sm bg-muted p-2 rounded flex items-center justify-between"
                      >
                        {ip}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyToClipboard(ip)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Location</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{searchResult.location}</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">ISP</Label>
                  <div className="text-sm mt-1">{searchResult.isp}</div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Badge variant="outline" className="mt-1">
                    {searchResult.category}
                  </Badge>
                </div>

                <div>
                  <Label className="text-sm font-medium">Last Seen</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {new Date(searchResult.lastSeen).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Information Tabs */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Information</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="dns" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="dns">DNS Records</TabsTrigger>
                  <TabsTrigger value="security">Security</TabsTrigger>
                  <TabsTrigger value="threats">Threats</TabsTrigger>
                  <TabsTrigger value="actions">Actions</TabsTrigger>
                </TabsList>

                {/* DNS Records Tab */}
                <TabsContent value="dns" className="space-y-4">
                  <div className="space-y-3">
                    {searchResult.dnsRecords.map((record, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Badge variant="outline">{record.type}</Badge>
                          <div className="font-mono text-sm">
                            {record.value}
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          TTL: {record.ttl}s
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Security Tab */}
                <TabsContent value="security" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-3">
                      <h4 className="font-medium">DNS Security</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">DNSSEC</span>
                          <Badge
                            variant={
                              searchResult.securityInfo.hasDNSSEC
                                ? "default"
                                : "secondary"
                            }
                          >
                            {searchResult.securityInfo.hasDNSSEC
                              ? "Enabled"
                              : "Disabled"}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">SSL Certificate</span>
                          <Badge
                            variant={
                              searchResult.securityInfo.hasSSL
                                ? "default"
                                : "secondary"
                            }
                          >
                            {searchResult.securityInfo.hasSSL
                              ? "Valid"
                              : "Invalid"}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">SSL Grade</span>
                          <Badge
                            className={getSSLGradeColor(
                              searchResult.securityInfo.sslGrade
                            )}
                          >
                            {searchResult.securityInfo.sslGrade}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium">Security Status</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {searchResult.securityInfo.malwareDetected ? (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-sm">
                            {searchResult.securityInfo.malwareDetected
                              ? "Malware Detected"
                              : "No Malware"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {searchResult.securityInfo.phishingDetected ? (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-sm">
                            {searchResult.securityInfo.phishingDetected
                              ? "Phishing Detected"
                              : "No Phishing"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Threats Tab */}
                <TabsContent value="threats" className="space-y-4">
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Threat Assessment</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Overall Risk</span>
                          <Badge
                            className={getThreatLevelColor(
                              searchResult.threatLevel
                            )}
                          >
                            {searchResult.threatLevel.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Reputation Score</span>
                          <span
                            className={`font-medium ${getReputationColor(
                              searchResult.reputation
                            )}`}
                          >
                            {searchResult.reputation}/100
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Block Count</span>
                          <span className="font-medium">
                            {searchResult.blockCount}
                          </span>
                        </div>
                      </div>
                    </div>

                    {searchResult.threatLevel !== "low" && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-yellow-800 dark:text-yellow-200">
                              This domain shows signs of potential threats.
                            </p>
                            <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                              Consider blocking or monitoring this domain
                              closely.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions" className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Button variant="destructive" className="w-full">
                      <Ban className="h-4 w-4 mr-2" />
                      Block Domain
                    </Button>
                    <Button variant="default" className="w-full">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Whitelist Domain
                    </Button>
                    <Button variant="outline" className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Policy
                    </Button>
                    <Button variant="outline" className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-2">External Tools</h4>
                    <div className="grid gap-2 md:grid-cols-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        VirusTotal
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        URLVoid
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Google Safe Browsing
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Cisco Talos
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
